/**
 * @module       utils
 * @description  Utility functions.
 *
 * @author  William Huster <william@aspire.is>
 */

const Utils = {
  toSnakeCase,
  toCamelCase,
  objectToCamelCase,
  objectToSnakeCase,
  isObject,
  isDefined,
  formatNumberAsUSD,
  formatDateShort,
  formatDateLong,
  formatDateTimeZone,
  textToKabobCase,
  getSubdomain,
  translateToCurrentLang,
  debugLang,
  formatText,
  formatValue,
  supportedLanguages,
  datePickerFormat,
}

export default Utils

/**
 * True/False, the given value is defined.
 **/
export function isDefined(value) {
  return typeof value !== 'undefined'
}

/**
 * toSnakeCase
 * Transform a string value from `camelCase` style notation to `snake_case` notation.
 * This is useful for translating Python-serialized JSON objects to JavaScript objects.
 */
export function toSnakeCase(value) {
  let upperChars = value.match(/([A-Z])/g)

  if (!upperChars) {
    return value
  }

  let str = value.toString()
  for (let i = 0, n = upperChars.length; i < n; i++) {
    str = str.replace(new RegExp(upperChars[i]), '_' + upperChars[i].toLowerCase())
  }

  if (str.slice(0, 1) === '_') {
    str = str.slice(1)
  }

  return str
}

/**
 * Transform a string value from `snake_case` style notation to `camelCase` notation.
 * This is useful for translating Python-serialized JSON objects to JavaScript objects.
 *
 * @param {String} value - The string value to transform.
 */
export function toCamelCase(value) {
  if (value === value.toUpperCase()) return value

  return value
    .split('_')
    .map(function (word, index) {
      // Do nothing with the first word
      if (index === 0) {
        return word
      }
      // If it is not the first word only upper case the first char and lowercase the rest.
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    })
    .join('')
}

/**
 * Check a value whether it is an Object
 */

export function isObject(value) {
  return value !== null && value instanceof Object && !Array.isArray(value)
}

/**
 * Check whether a value is an Object or Array
 */
export function isObjectOrArray(value) {
  return value !== null && value instanceof Object
}

/**
 * Transform the string-based keys of a JavaScript object to `camelCase` style notation.
 * This is useful for translating the style of object keys after making an API call to
 * the Python-based API, which uses `snake_case` style notation by default.
 *
 * Works on both objects, arrays, and any combination of nested objects and arrays.
 */
export function objectToCamelCase(obj) {
  if (isObject(obj)) {
    return Object.keys(obj).reduce((acc, snakeKey) => {
      const camelKey = toCamelCase(snakeKey)
      acc[camelKey] = isObjectOrArray(obj[snakeKey])
        ? objectToCamelCase(obj[snakeKey])
        : obj[snakeKey]
      return acc
    }, {})
  }
  if (Array.isArray(obj)) {
    let index = 0
    return obj.reduce((acc, val, index) => {
      acc[index] = isObjectOrArray(val) ? objectToCamelCase(val) : val
      return acc
    }, [])
  }
}

/**
 * Transform the string-based keys of a JavaScript object to `snake_case` style notation.
 * This is useful for translating the `camelCase` style of JavaScript keys BEFORE posting
 * the data to the Python-based API, which uses `snake_case` style notation by default.
 */
export function objectToSnakeCase(value) {
  if (isObject(value)) {
    return Object.keys(value).reduce((acc, camelKey) => {
      const snakeKey = toSnakeCase(camelKey)
      acc[snakeKey] = isObject(value[camelKey])
        ? objectToSnakeCase(value[camelKey])
        : value[camelKey]
      return acc
    }, {})
  }
}

/**
 *@function       debounce
 *@description    delay calling of callback function on set time
 *@params         callback <Function> : a function is being returned; time <Number> : delay time in milliseconds
 *@returns        {Function}
 */

export function debounce(callback, time) {
  let timer = null
  return function (...args) {
    const onComplete = () => {
      callback.apply(this, args)
      timer = null
    }

    if (timer) {
      clearTimeout(timer)
    }

    timer = setTimeout(onComplete, time)
  }
}

/**
 * Returns a alphabetically sorted array of Objects, sorted by a 'property' (String) and excluding a specified 'exception' (String)
 */
export function sortAlphabetically(arr, property, exception = null) {
  arr.sort(function (a, b) {
    if (a[property] !== exception && b[property] !== exception) {
      var textA = a[property].toUpperCase()
      var textB = b[property].toUpperCase()
      return textA < textB ? -1 : textA > textB ? 1 : 0
    }
  })
  return arr
}

/**
 * @function    getSubdomain
 * parses subdomain from window.location.hostname
 */
export function getSubdomain() {
  let hostnameArr = window.location.hostname.split('.')
  return hostnameArr.length === 3 ? hostnameArr[0] : 'n/a'
}

/**
 * @function    textToKabobCase
 * Takes a string of text (e.g. 'Financial Health') and turns it into its kabob-case counterpart (e.g. 'financial-health')
 * @param   {String}  text  - custom string that functions as action being tracked
 **/
export function textToKabobCase(text) {
  return text
    .toLowerCase()
    .split(' ')
    .join('-')
}

/**
 * Format a number as US Dollars
 *
 * @param {String, Number} value - The value to format as US Dollars
 */
export function formatNumberAsUSD(value, hideCents) {
  // Ignore falsey values, except zero, because zero dollars is OK!
  if (value !== 0 && !value) return ''
  let str = Number(value).toLocaleString('en', {
    style: 'currency',
    currency: 'USD',
  })
  if (hideCents) {
    str = str.split('.')[0]
  }
  return str
}

export function currencyFormat(value, currentLang, noCents = true) {
  if (value !== 0 && !value) return ''
  let codeMap = {en: 'USD', es: 'USD'}
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: codeMap[currentLang],
    maximumFractionDigits: noCents ? 0 : 2,
  }).format(value)
}

export function datePickerFormat(value) {
  if (!value) {
    return 'N/A'
  }

  var monthString = `${value.getUTCMonth() + 1}`
  if (monthString.length == 1) {
    monthString = '0' + monthString
  }
  return `${monthString}\/${value.getUTCDate()}\/${value.getUTCFullYear()}`
}
/**
 * Format a Date string or object.
 *
 * Returns 'N/A' if a falsey value is given.
 *
 * @param {String, Date} value - The date string or object to format.
 * @param {String} lang - the chosen language to translate the date into
 */
export function formatDateShort(value, lang = 'en') {
  if (!value) return 'N/A'
  const date = new Date(value)
  const locale = lang.replace('_', '-') || 'en'
  return date.toLocaleDateString([locale], {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  })
}

export function formatDateLong(value, lang = 'en') {
  if (!value) return 'N/A'
  const locale = lang.replace('_', '-') || 'en'
  const date = new Date(value)
  return date.toLocaleString([locale], {
    dateStyle: 'long',
    timeStyle: 'short',
  })
}

export function formatDateTimeZone(value) {
  if (!value) return 'N/A'
  const date = new Date(value)
  return new Intl.DateTimeFormat('en', {
    dateStyle: 'short',
    timeStyle: 'long',
    hour12: false,
  }).format(date)
}

/**
 * Format translations based on current language
 *
 * Returns english translation if no option is given
 *
 * @param {String} lang - The language that the app is in currently
 * @param {String} valueEs - the text to return if language is spanish
 * @param {String} valueEn - the text to return if it is in english
 */

export function translateToCurrentLang(lang = 'en', valueEs, valueEn) {
  return lang === 'es' && valueEs ? valueEs : valueEn
}

export function debugLang(value, language, surveyResponse) {
  value
}

export function formatValue(textValue, varMap) {
  const additionalCustoms = {
    'organization.name': varMap['org'].name,
    organizationName: varMap['org'].name,
    'organization.grantLimit': currencyFormat(varMap['org'].grantLimit, 'en', true),
    organizationGrantLimit: formatNumberAsUSD(varMap['org'].grantLimit),
    'organization.supportEmail': varMap['org'].supportEmail,
    'organization.rollingGrantDuration': varMap['org'].rollingGrantDuration,
    organizationUrl: varMap['org'].url,
    employeeName: varMap['user'] && varMap['user'].firstName,
    employeePayPalEmail: varMap['user'] && varMap['user'].paypalEmail,
    adminName: varMap['admin'] && varMap['admin'].firstName,
    approvedFundsDisplay: varMap['request'] && varMap['request'].approvedFundsDisplay,
    requestAmountDisplay: varMap['request'] && varMap['request'].amountDisplay,
  }

  return textValue.formatUnicorn({
    ...varMap['org'].customVariables,
    ...additionalCustoms,
  })
}

export function formatText(lang, textMap, varMap) {
  const additionalCustoms = {
    'organization.name': varMap['org'].name,
    organizationName: varMap['org'].name,
    'organization.grantLimit': currencyFormat(varMap['org'].grantLimit, lang, true),
    organizationGrantLimit: formatNumberAsUSD(varMap['org'].grantLimit),
    'organization.supportEmail': varMap['org'].supportEmail,
    'organization.rollingGrantDuration': varMap['org'].rollingGrantDuration,
    organizationUrl: varMap['org'].url,
    employeeName: varMap['user'] && varMap['user'].firstName,
    employeePayPalEmail: varMap['user'] && varMap['user'].paypalEmail,
    adminName: varMap['admin'] && varMap['admin'].firstName,
    approvedFundsDisplay: varMap['request'] && varMap['request'].approvedFundsDisplay,
    requestAmountDisplay: varMap['request'] && varMap['request'].amountDisplay,
  }
  switch (lang) {
    case 'en':
      return textMap['en'].formatUnicorn({
        ...varMap['org'].customVariables,
        ...additionalCustoms,
      })
    case 'es':
      return textMap['es'].formatUnicorn({
        ...varMap['org'].customVariables,
        ...additionalCustoms,
      })
    default:
      console.log('Language has been set to an unknown language.')
  }
}

//Takes hex values from our database i.e. 114ba7 and returns the hue for HSL
//colors for the svg's

export function hexToHsl(hexValue) {
  // Convert hex to RGB first
  let r = 0,
    g = 0,
    b = 0

  if (hexValue.length == 6) {
    r = '0x' + hexValue[0] + hexValue[1]
    g = '0x' + hexValue[2] + hexValue[3]
    b = '0x' + hexValue[4] + hexValue[5]
  }
  // Then to HSL
  r /= 255
  g /= 255
  b /= 255
  let cmin = Math.min(r, g, b),
    cmax = Math.max(r, g, b),
    delta = cmax - cmin,
    h = 0,
    s = 0,
    l = 0

  if (delta == 0) h = 0
  else if (cmax == r) h = ((g - b) / delta) % 6
  else if (cmax == g) h = (b - r) / delta + 2
  else h = (r - g) / delta + 4

  h = Math.round(h * 60)

  if (h < 0) h += 360

  return h
}

// This code comes from stackoverflow as a means to format text similar to python
String.prototype.formatUnicorn =
  String.prototype.formatUnicorn ||
  function () {
    'use strict'
    var str = this.toString()
    if (arguments.length) {
      var t = typeof arguments[0]
      var key
      var args =
        'string' === t || 'number' === t ? Array.prototype.slice.call(arguments) : arguments[0]

      for (key in args) {
        str = str.replace(new RegExp('\\{\\{' + key + '\\}\\}', 'gi'), args[key])
      }
    }

    return str
  }

export const supportedLanguages = [
  {
    languageKey: 'en',
    language: 'English - en',
  },
  {
    languageKey: 'es',
    language: 'Spanish - es',
  },
]

//{
//languageKey: 'fr',
//language: 'French - fr',
//},
//{
//languageKey: 'ja',
//language: 'Japanese - ja',
//},
//{
//languageKey: 'de',
//language: 'German - de',
//},
